import moment from 'jalali-moment'
import { faPlain } from 'utilities/helper/format'

/**
 * Wording and shapes for «اطلاع‌رسانی به اعضا».
 *
 * Pure on purpose: every label a manager reads on this feature is decided
 * here, so the four screens cannot drift into calling the same channel two
 * different things, and the boundaries («دیروز» versus «۲۳ ساعت پیش») can be
 * pinned by tests instead of by looking at the page on a Tuesday.
 */

/** Counts, as digits a Persian reader reads: faPlain is the app's formatter. */
export const faCount = (value: number) => faPlain.format(Number(value || 0))

/**
 * Latin digits inside an already-formatted string, folded to Persian ones.
 *
 * `faCount` covers numbers we hold as numbers; this covers the Jalali stamps,
 * which moment hands over as text with the separators already in place.
 */
export const faDigits = (value: string) =>
    value.replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])

/**
 * Every instant in this log is ISO-8601, and is parsed as nothing else.
 *
 * Bare `moment(text)` falls through to `new Date(text)` for anything it does
 * not recognise, which is both a deprecation warning and a guess; asked for
 * ISO-8601 it simply reports that the string was not one.
 */
const parse = (iso: string) => moment(iso, moment.ISO_8601)

/** «۱۴۰۵/۰۶/۲۶» from an ISO-8601 instant; empty for anything unparseable. */
export const jalaliDate = (iso?: string | null) => {
    if (!iso) return ''
    const at = parse(iso)
    return at.isValid() ? faDigits(at.locale('fa').format('jYYYY/jMM/jDD')) : ''
}

/**
 * «۱۴۰۵/۰۶/۲۶ ساعت ۱۰:۳۳».
 *
 * The word «ساعت» is load-bearing, not decoration. A bare «۱۴۰۵/۰۶/۲۶ ۱۰:۳۳»
 * is two separate digit runs in one right-to-left line, and which of them a
 * reader takes for the date is then a matter of habit — the جزئیات screen
 * shipped exactly that and read backwards. One Persian word between them
 * settles it.
 */
export const jalaliDateTime = (iso?: string | null) => {
    if (!iso) return ''
    const at = parse(iso)
    if (!at.isValid()) return ''
    const stamp = at.locale('fa').format('jYYYY/jMM/jDD HH:mm')
    const [date, clock] = stamp.split(' ')
    return `${faDigits(date)} ساعت ${faDigits(clock)}`
}

/**
 * How long ago, in the words the people list needs.
 *
 * Under a day it counts hours, because «دیروز» for something sent ninety
 * minutes ago (at 23:30, read at 01:00) is wrong in the only way that matters
 * here — the manager is deciding whether Hamyan has already nagged this
 * member. Past a week the date is more use than «۹ روز پیش».
 */
export const relativeTime = (iso?: string | null, now?: number | Date) => {
    if (!iso) return ''
    const at = parse(iso)
    if (!at.isValid()) return ''
    const reference = now === undefined ? moment() : moment(now)

    const seconds = reference.diff(at, 'seconds')
    // negative = a timestamp in the future, i.e. clock skew; «همین حالا» is
    // the only honest thing to say about it
    if (seconds < 60) return 'همین حالا'

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${faCount(minutes)} دقیقه پیش`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${faCount(hours)} ساعت پیش`

    const days = reference
        .clone()
        .startOf('day')
        .diff(at.clone().startOf('day'), 'days')
    if (days <= 1) return 'دیروز'
    if (days < 7) return `${faCount(days)} روز پیش`
    return jalaliDate(iso)
}

const CHANNEL_LABELS: { [channel in TMessagingChannel]: string } = {
    sms: 'پیامک',
    eitaa: 'ایتا',
    bale: 'بله',
    telegram: 'تلگرام',
    push: 'اعلان',
    inbox: 'پیام درون‌برنامه',
}

/** Counts are kept per group, and a group is a word of its own. */
const GROUP_LABELS: { [group in TMessagingChannelGroup]: string } = {
    sms: 'پیامک',
    messenger: 'پیام‌رسان',
    push: 'اعلان',
    inbox: 'پیام درون‌برنامه',
}

/** A channel this build has never heard of prints its own key, not nothing. */
export const channelLabel = (channel: string) =>
    CHANNEL_LABELS[channel as TMessagingChannel] ?? channel

export const groupLabel = (group: string) =>
    GROUP_LABELS[group as TMessagingChannelGroup] ?? group

export interface IChannelChip {
    key: string
    label: string
    failed: boolean
    /** The member tapped the link inside the message. */
    seen: boolean
    caption: string | null
}

/**
 * One chip per delivery, in the order the backend lists them.
 *
 * The caption is the whole story of a fallback: «ایتا ناموفق» beside «پیامک —
 * جایگزین پیام‌رسان» is Hamyan saying it tried the free channel, was refused,
 * and paid for an SMS. Without it the pair reads as two separate sends.
 */
export const channelChips = (
    channels?: IMessagingChannelDelivery[] | null
): IChannelChip[] =>
    (channels ?? []).map((delivery, index) => ({
        key: `${delivery.channel}-${index}`,
        label: channelLabel(delivery.channel),
        failed: delivery.state === 'failed',
        seen: !!delivery.opened_at,
        caption:
            delivery.is_fallback && delivery.group !== 'messenger'
                ? 'جایگزین پیام‌رسان'
                : null,
    }))

/** Fixed order, so two cards never list the same channels differently. */
const GROUP_ORDER: TMessagingChannelGroup[] = [
    'sms',
    'messenger',
    'push',
    'inbox',
]

export interface IChannelCount {
    key: TMessagingChannelGroup
    label: string
    count: number
    text: string
}

/** «پیامک ۴», «پیام‌رسان ۳» — groups that carried nothing are left out. */
export const channelCounts = (
    counts?: TMessagingChannelCounts | null
): IChannelCount[] =>
    GROUP_ORDER.filter((group) => (counts?.[group] ?? 0) > 0).map((group) => {
        const count = counts?.[group] ?? 0
        return {
            key: group,
            label: GROUP_LABELS[group],
            count,
            text: `${GROUP_LABELS[group]} ${faCount(count)}`,
        }
    })

/** «۷ پیام در ۳۰ روز اخیر» — and a sentence, not «۰ پیام», when there is none. */
export const recentLine = (messages: number, days: number) =>
    messages > 0
        ? `${faCount(messages)} پیام در ${faCount(days)} روز اخیر`
        : `در ${faCount(days)} روز اخیر پیامی ارسال نشده است`

/**
 * «قسط وام، ۲ ساعت پیش» — what a member was last told, and when.
 *
 * The separator is a Persian comma and NOT a middle dot. A «·» is drawn almost
 * exactly like the Persian zero «۰», and the relative time usually starts with
 * a digit: «یادآوری قسط · ۱ ساعت پیش» was read — on the real screen — as
 * «۱۰ ساعت پیش», and three days as «۳۰ روز پیش». Never put a dot-like
 * separator next to a number in Persian text.
 */
export const lastLine = (
    message: IMessagingMessage,
    now?: number | Date
): string => {
    const when = relativeTime(message.at, now)
    const title = message.title || 'اطلاع‌رسانی'
    return when ? `${title}، ${when}` : title
}

export const recipientsLine = (count: number) => `${faCount(count)} گیرنده`

export const openedLine = (count: number) => `${faCount(count)} نفر دیدند`

export const failedLine = (count: number) => `${faCount(count)} ناموفق`
